import { useState, useCallback, useEffect } from 'react';
import { SamedayCounty, SamedayCity } from '../types/nomenclator';
import { nomenclatorService } from '../services/NomenclatorService';

interface UseNomenclatorReturn {
  counties: SamedayCounty[];
  cities: Record<number, SamedayCity[]>;
  isLoading: boolean;
  error: string | null;
  preloadNomenclator: () => Promise<void>;
  getCitiesForCounty: (countyId: number) => Promise<void>;
}

export const useNomenclator = (): UseNomenclatorReturn => {
  const [counties, setCounties] = useState<SamedayCounty[]>([]);
  const [cities, setCities] = useState<Record<number, SamedayCity[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const preloadNomenclator = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const countiesData: SamedayCounty[] = await nomenclatorService.getCounties();
      setCounties(countiesData);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      console.error('Error preloading nomenclator:', err);
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies needed

  const getCitiesForCounty = useCallback(async (countyId: number) => {
    try {
      setIsLoading(true);
      setError(null);

      const citiesData: SamedayCity[] = await nomenclatorService.getCitiesForCounty(countyId);
      
      setCities(prev => ({
        ...prev,
        [countyId]: citiesData
      }));

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMessage);
      console.error('Error fetching cities:', err);
    } finally {
      setIsLoading(false);
    }
  }, []); // No dependencies needed

  // Load counties on mount only
  useEffect(() => {
    preloadNomenclator();
  }, []); // Run once on mount

  return {
    counties,
    cities,
    isLoading,
    error,
    preloadNomenclator,
    getCitiesForCounty
  };
};
